import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconClose } from "@/components/icons/PageActionIcons";
import "./confirm-dialog.css";

export type ConfirmOptions = {
  title?: string;
  message?: string;
  bodyHtml?: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  copyValue?: string;
  openUrl?: string;
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let globalConfirm: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;

/** v1 niceConfirm — promise-based confirm modal. */
export function niceConfirm(opts: ConfirmOptions): Promise<boolean> {
  if (globalConfirm) return globalConfirm(opts);
  return Promise.resolve(window.confirm(opts.message || opts.title || "Confirm?"));
}

export default function ConfirmDialogHost() {
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  const finish = useCallback((ok: boolean) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    p?.resolve(ok);
  }, []);

  useEffect(() => {
    globalConfirm = (opts) =>
      new Promise<boolean>((resolve) => {
        const p = { ...opts, resolve };
        pendingRef.current = p;
        setPending(p);
      });
    return () => {
      globalConfirm = null;
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") finish(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, finish]);

  const onBodyClick = async (ev: React.MouseEvent) => {
    const t = ev.target as HTMLElement;
    const copyUrlBtn = t.closest?.("[data-cf-copy-url]") as HTMLElement | null;
    const openUrlBtn = t.closest?.("[data-cf-open-url]") as HTMLElement | null;
    const copyBtn = t.closest?.("[data-cf-copy]") as HTMLElement | null;
    const openBtn = t.closest?.("[data-cf-open]") as HTMLElement | null;

    const copyText = async (v: string, btn: HTMLElement, label = "Copy") => {
      if (!v) return;
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(v);
        else {
          const ta = document.createElement("textarea");
          ta.value = v;
          ta.style.cssText = "position:fixed;left:-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        btn.textContent = "Copied";
        setTimeout(() => {
          btn.textContent = label;
        }, 900);
      } catch {
        /* ignore */
      }
    };

    if (copyUrlBtn) {
      await copyText(copyUrlBtn.getAttribute("data-cf-copy-url") || "", copyUrlBtn);
      return;
    }
    if (openUrlBtn) {
      const u = openUrlBtn.getAttribute("data-cf-open-url") || "";
      if (u) window.open(u, "_blank", "noopener,noreferrer");
      return;
    }
    if (copyBtn && pending) {
      await copyText(pending.copyValue || "", copyBtn);
      return;
    }
    if (openBtn && pending?.openUrl) {
      window.open(pending.openUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (!pending) return null;

  const { title, message, bodyHtml, okLabel, cancelLabel, danger } = pending;

  return createPortal(
    <div id="confirmModal" className="on" role="presentation">
      <div className="cfBackdrop" onClick={() => finish(false)} />
      <div className="cfCard" role="dialog" aria-modal="true" aria-label="Confirm">
        <div className="cfTitleRow">
          <div className="cfTitle">{title || "Confirm"}</div>
          <button className="iconBtn" type="button" title="Close" onClick={() => finish(false)}>
            <IconClose />
          </button>
        </div>
        <div className="cfBody" onClick={(e) => void onBodyClick(e)}>
          {bodyHtml ? (
            <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          ) : (
            <div>{message}</div>
          )}
        </div>
        <div className="cfActions">
          <button className="btn" type="button" onClick={() => finish(false)}>
            {cancelLabel || "Cancel"}
          </button>
          <button
            className="primaryBtn"
            type="button"
            style={
              danger
                ? { borderColor: "rgba(239,68,68,0.30)", background: "rgba(239,68,68,0.14)" }
                : undefined
            }
            onClick={() => finish(true)}
          >
            {okLabel || "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
